/* eslint-disable max-lines */
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  ActionsGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dropdown,
  FlatButton,
  NumberInput,
  RaisedButton,
  TitleText,
} from "@evergis/ui";

// eslint-disable-next-line max-len
import { ClassificationAttribute } from "../../../Classification/components/ClassificationAttribute/ClassificationAttribute";
import {
  ClassificationAttributeContainer,
  ClassificationDetailsContainer,
  ClassificationDialogContainer,
  Dash,
} from "../styled";
import {
  ALGORITHM_CLASS_COUNT,
  CLASSES_RANGE_CALC_DEFAULTS,
  MAX_OBJECTS_COUNT,
  MIN_OBJECTS_COUNT,
  RANGE_DEFAULT,
  RANGE_MIN,
} from "../constants";
import { useAction } from "../../../../hooks/useAction";
import { goToClassification } from "../../../../ducks/location/location";
import { getClassificationField, getClassificationParameter } from "../../../../ducks/classification/selectors";
import { isClusterFillColor as checkIsClusterFillColor } from "../../../../interfaces/style";
import { getValueIndex } from "../../../../ducks/style/selectors";
import { getAppConfig } from "../../../../ducks/appConfig";
import { getAttributeTypeAlgorithms } from "../utils/getAttributeTypeAlgorithms";
import { getMaxClassifyClasses } from "../utils/getMaxClassifyClasses";
import { setClassificationError } from "../../../../ducks/classification/classification";
import { useClassificationQueryParams } from "../../../../hooks/location/useClassificationQueryParams";
import { useCurrentLayerAttribute } from "../../../../hooks/layer/useCurrentLayerAttribute";
import { useClassificationSegment } from "../hooks/useClassificationSegment";
import { useSegmentStyle } from "../../../../modules/Style/hooks/useSegmentStyle";
import { ClassifyType } from "../../../../interfaces/classification";
import { AlgorithmOption, ClassesRangeCalculationProps } from "../types";

export const ClassesRangeCalculationBase: FC<ClassesRangeCalculationProps> = props => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const appConfig = useSelector(getAppConfig);
  const { count, algorithm: inputAlgorithm, calculateClassesCount, toggle } = props;
  const [classesCount, setClassesCount] = useState(count || RANGE_MIN);
  const [algorithm, setAlgorithm] = useState(inputAlgorithm);
  const style = useSegmentStyle();
  const {
    parameter: queryParameter,
    attributeName,
    valueIndex: queryValueIndex,
    type,
    minQuantity: queryMinQuantity,
    maxQuantity: queryMaxQuantity,
  } = useClassificationQueryParams();
  const classify = useAction(goToClassification);
  const field = useSelector(getClassificationField);
  const parameter = useSelector(getClassificationParameter);
  const valueIndex = useSelector(getValueIndex);
  const classificationSegment = useClassificationSegment();
  const getParameter = useMemo(
    () => classificationSegment?.parameter || parameter || queryParameter,
    [classificationSegment?.parameter, parameter, queryParameter],
  );
  const isClusterFillColor = checkIsClusterFillColor(style.symbol, getParameter);
  const [minQuantity, setMinQuantity] = useState(queryMinQuantity || (isClusterFillColor ? MIN_OBJECTS_COUNT : 0));
  const [maxQuantity, setMaxQuantity] = useState(queryMaxQuantity || (isClusterFillColor ? MAX_OBJECTS_COUNT : 0));
  const getField = useMemo(
    () => classificationSegment?.field || field || attributeName,
    [attributeName, classificationSegment?.field, field],
  );
  const attribute = useCurrentLayerAttribute(getField, parameter);
  const typeAlgorithms = useMemo(() => getAttributeTypeAlgorithms(attribute?.type), [attribute?.type]);
  const inputWidth = isClusterFillColor ? "9.25rem" : "7.625rem";

  const prevField = useRef(getField);
  const prevCount = useRef(classesCount);
  const prevAlgorithm = useRef(algorithm);

  const isSubmitDisabled = useMemo(
    () =>
      !getField ||
      !classesCount ||
      !algorithm ||
      (prevField.current === getField && prevCount.current === classesCount && prevAlgorithm.current === algorithm),
    [algorithm, classesCount, getField],
  );

  const algorithms: AlgorithmOption[] = useMemo(
    () => [
      {
        text: t("classification.equalIntervals", { ns: "dialogs" }),
        value: "equalInterval" as ClassifyType,
      },
      {
        text: t("classification.naturalBreaks", { ns: "dialogs" }),
        value: "naturalBreaks" as ClassifyType,
      },
      {
        text: t("classification.quantile", { ns: "dialogs" }),
        value: "quantile" as ClassifyType,
      },
      {
        text: t("classification.uniqueValues", { ns: "dialogs" }),
        value: "unique" as ClassifyType,
      },
    ],
    [t],
  );

  const filteredAlgorithms = useMemo(() => {
    return algorithms.filter(({ value, hidden }) => typeAlgorithms.includes(value) && (!hidden || hidden(props)));
  }, [algorithms, props, typeAlgorithms]);

  const resetState = useCallback(() => {
    setClassesCount(null);
    setAlgorithm(null);
  }, []);

  const onApply = useCallback(() => {
    dispatch(setClassificationError(null));

    calculateClassesCount({
      classCount: +classesCount,
      algorithm,
    });

    toggle();
    resetState();
    classify({
      parameter: getParameter,
      field: getField,
      valueIndex: queryValueIndex || valueIndex,
      count: +classesCount,
      type: algorithm,
      minQuantity,
      maxQuantity,
    });
  }, [
    algorithm,
    calculateClassesCount,
    classesCount,
    classify,
    dispatch,
    getField,
    getParameter,
    maxQuantity,
    minQuantity,
    queryValueIndex,
    resetState,
    toggle,
    valueIndex,
  ]);

  const onCancel = useCallback(() => {
    resetState();
    toggle();
  }, [resetState, toggle]);

  const checkClassesCount = useCallback(
    e => {
      if (!e.target.rawValue) {
        setClassesCount(count || RANGE_MIN);
      }
    },
    [count],
  );

  const chooseAlgorithm = useCallback(options => {
    setAlgorithm(options[0].value);
    setClassesCount(options[0].value ? ALGORITHM_CLASS_COUNT[options[0].value] : RANGE_DEFAULT);
  }, []);

  const numberOfClasses = useMemo(
    () => (
      <NumberInput
        value={classesCount}
        width={inputWidth}
        label={t("classification.numberOfClasses", { ns: "dialogs" })}
        min={RANGE_MIN}
        max={getMaxClassifyClasses(algorithm, appConfig)}
        zIndex={110}
        disabled={!getField}
        counter
        onChange={setClassesCount}
        onBlur={checkClassesCount}
      />
    ),
    [algorithm, appConfig, checkClassesCount, classesCount, getField, inputWidth, t],
  );

  useEffect(() => {
    if (count) {
      setClassesCount(count);
    }
  }, [count]);

  useEffect(() => {
    if (type) {
      setAlgorithm(type);
    }
  }, [type]);

  useEffect(() => {
    setAlgorithm(inputAlgorithm || filteredAlgorithms[0].value);
  }, [filteredAlgorithms, inputAlgorithm]);

  useEffect(() => {
    if (!typeAlgorithms.includes(algorithm)) {
      setAlgorithm(filteredAlgorithms[0]?.value);
      setClassesCount(
        filteredAlgorithms[0]?.value ? ALGORITHM_CLASS_COUNT[filteredAlgorithms[0]?.value] : RANGE_DEFAULT,
      );
    }
    prevField.current = getField;
  }, [getField]);

  useEffect(() => {
    prevAlgorithm.current = algorithm;
  }, [algorithm]);

  useEffect(() => {
    prevCount.current = classesCount;
  }, [classesCount]);

  return (
    <Dialog
      isOpen
      maxWidth={CLASSES_RANGE_CALC_DEFAULTS.maxWidth}
      minWidth={isClusterFillColor ? "25.5rem" : "41rem"}
      onCloseRequest={onCancel}
      style={{ flex: 0 }}
    >
      <DialogTitle>
        <TitleText>{t("classification.dialogHeader", { ns: "dialogs" })}</TitleText>
      </DialogTitle>
      <DialogContent>
        <ClassificationDialogContainer>
          {!isClusterFillColor && (
            <ClassificationAttributeContainer>
              <ClassificationAttribute />
            </ClassificationAttributeContainer>
          )}
          {!isClusterFillColor ? (
            <ClassificationDetailsContainer>
              <Dropdown
                width={"25rem"}
                label={t("classification.algorithm", { ns: "dialogs" })}
                placeholder={t("classification.algorithm", { ns: "dialogs" })}
                options={filteredAlgorithms}
                value={algorithm}
                onChange={chooseAlgorithm}
                zIndex={110}
                disabled={!getField}
              />
              {numberOfClasses}
            </ClassificationDetailsContainer>
          ) : (
            <>
              <ClassificationDetailsContainer>
                <NumberInput
                  value={minQuantity}
                  width={inputWidth}
                  label={t("classification.minQuantity", { ns: "dialogs" })}
                  min={MIN_OBJECTS_COUNT}
                  max={MAX_OBJECTS_COUNT}
                  zIndex={110}
                  disabled={!getField}
                  counter
                  onChange={setMinQuantity}
                />
                <Dash>-</Dash>
                <NumberInput
                  value={maxQuantity}
                  width={inputWidth}
                  label={t("classification.maxQuantity", { ns: "dialogs" })}
                  min={MIN_OBJECTS_COUNT}
                  max={MAX_OBJECTS_COUNT}
                  zIndex={110}
                  disabled={!getField}
                  counter
                  onChange={setMaxQuantity}
                />
              </ClassificationDetailsContainer>
              <ClassificationDetailsContainer>{numberOfClasses}</ClassificationDetailsContainer>
            </>
          )}
        </ClassificationDialogContainer>
      </DialogContent>
      <DialogActions>
        <ActionsGroup>
          <RaisedButton primary onClick={onApply} disabled={!isClusterFillColor && isSubmitDisabled}>
            {t("actions.calculate", { ns: "common" })}
          </RaisedButton>
          <FlatButton onClick={onCancel}>{t("actions.cancel", { ns: "common" })}</FlatButton>
        </ActionsGroup>
      </DialogActions>
    </Dialog>
  );
};
